/**
 * AuraFlow domain constants.
 *
 * Every value here was recovered from the production app (source + live
 * records), not invented. Costs are verified against all 10 historical
 * energy-field rows.
 */

export const PLANS = {
  basic:   { tier: 'basic',   name: 'Basic Shield',  priceCents: 2900, monthlyTokens: 100, deviceLimit: 1, switchQuota: 1, energyReserve: 70,  connectionStrength: 65 },
  premium: { tier: 'premium', name: 'Premium Aura',  priceCents: 5900, monthlyTokens: 200, deviceLimit: 2, switchQuota: 2, energyReserve: 85,  connectionStrength: 80 },
  crystal: { tier: 'crystal', name: 'Crystal Master', priceCents: 9900, monthlyTokens: 500, deviceLimit: 3, switchQuota: 3, energyReserve: 100, connectionStrength: 95 },
};

/** Field intensities. `hours` is how long the field lasts once built. */
/** Public web address — invite links point here so they work from any platform. */
export const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://spirit-spirit-prod.waserd.com';

export const INTENSITIES = {
  spark:     { key: 'spark',     name: 'Spark',     hours: 2,   baseCost: 5   },
  glow:      { key: 'glow',      name: 'Glow',      hours: 24,  baseCost: 20  },
  beacon:    { key: 'beacon',    name: 'Beacon',    hours: 168, baseCost: 100 },
  sanctuary: { key: 'sanctuary', name: 'Sanctuary', hours: 720, baseCost: 280 },
};

export const RADII = [
  { meters: 15, multiplier: 0.7 },
  { meters: 30, multiplier: 1.0 },
  { meters: 50, multiplier: 1.8 },
];

/** Making a field public shares it with everyone, so it costs 20% less. */
export const PUBLIC_DISCOUNT = 0.2;

/** Grace ring around a field's edge in which check-in is still permitted. */
export const CHECKIN_GRACE_M = 20;

/** Re-energising an existing field. Public fields cost more but extend less. */
export const REENERGIZE = {
  private: { costFactor: 0.10, durationFactor: 1.0 },
  public:  { costFactor: 0.20, durationFactor: 0.5 },
};

/** Build time: one minute per 10 metres of radius (15m→90s, 30m→180s, 50m→300s). */
export function buildSeconds(radiusMeters) {
  return (radiusMeters / 10) * 60;
}

/** cost = base x radius multiplier x public discount, rounded. */
export function fieldCost({ intensity, radiusMeters, isPublic }) {
  const base = INTENSITIES[intensity]?.baseCost;
  const radius = RADII.find((r) => r.meters === radiusMeters);
  if (base === undefined) throw new Error(`unknown intensity: ${intensity}`);
  if (!radius) throw new Error(`unknown radius: ${radiusMeters}`);
  return Math.round(base * radius.multiplier * (isPublic ? 1 - PUBLIC_DISCOUNT : 1));
}

export function reenergizeCost(field) {
  const rule = field.is_public ? REENERGIZE.public : REENERGIZE.private;
  return Math.max(1, Math.round(field.token_cost * rule.costFactor));
}

export function reenergizeHours(field) {
  const rule = field.is_public ? REENERGIZE.public : REENERGIZE.private;
  return INTENSITIES[field.intensity].hours * rule.durationFactor;
}
